import { invariant } from "../core/assert";
import { addSafe } from "../core/int";
import { type Cents, type CompanyId, type GameTime, id } from "../core/units";
import type { SimContext } from "../sim/context";
import type { WorldState } from "../sim/state";

/** Analytic accounts (economy.md § 3.1). Grows with the modules; never rename a published one. */
export type Account =
  | "equity.opening"
  | "revenue.contract"
  | "revenue.bonus"
  | "revenue.indexation"
  | "revenue.insurance"
  | "penalty.late"
  | "penalty.break"
  | "penalty.failed"
  | "cost.fuel"
  | "cost.wages"
  | "cost.tolls"
  | "cost.rent"
  | "cost.maintenance"
  | "cost.insurance"
  | "cost.overhead"
  | "cost.licence"
  | "cost.subcontract"
  | "cost.fine"
  | "capex.vehicle"
  | "capex.infrastructure"
  | "cost.warehouse"
  | "disposal.vehicle"
  | "finance.interest";

/** Income-statement section of each account; `null` = outside the statement (equity, capex). */
const SECTION: Readonly<Record<Account, "revenue" | "cost" | null>> = {
  "equity.opening": null,
  "revenue.contract": "revenue",
  "revenue.bonus": "revenue",
  /** Fuel adjustment of indexed contracts, either way (SER-274). */
  "revenue.indexation": "revenue",
  /** Insured losses refunded (SER-277). */
  "revenue.insurance": "revenue",
  "penalty.late": "revenue",
  /** Early end of a recurring agreement (SER-272). */
  "penalty.break": "revenue",
  /** Contracts taken and not delivered in time (SER-313). */
  "penalty.failed": "revenue",
  "cost.fuel": "cost",
  "cost.wages": "cost",
  "cost.tolls": "cost",
  "cost.rent": "cost",
  "cost.maintenance": "cost",
  "cost.insurance": "cost",
  "cost.overhead": "cost",
  /** Licence entry fees (SER-277). */
  "cost.licence": "cost",
  /** Runs handed over to subcontractors (SER-278). */
  "cost.subcontract": "cost",
  /** Low-emission zone fines (SER-307). */
  "cost.fine": "cost",
  "capex.vehicle": null,
  "capex.infrastructure": null,
  "cost.warehouse": "cost",
  /** Sale of an owned vehicle at its book value (no gain or loss: SER-269). */
  "disposal.vehicle": null,
  "finance.interest": "cost",
};
export const ACCOUNTS = Object.keys(SECTION) as Account[];

/**
 * One ledger entry: money in (> 0) or out (< 0) for a company, against the outside world
 * (no double entry yet). Entries are append-only and frozen once posted.
 */
export type LedgerEntry = {
  readonly id: string;
  readonly companyId: CompanyId;
  readonly time: GameTime;
  readonly account: Account;
  readonly amount: Cents;
  /** What the entry is about (contract, trip, vehicle id…), for drill-down. */
  readonly ref: string;
};

export type LedgerState = {
  readonly entries: LedgerEntry[];
  nextSeq: number;
};

export type Posting = {
  readonly companyId: CompanyId;
  readonly account: Account;
  readonly amount: Cents;
  readonly ref: string;
};

/** Appends an entry at the current game time. The only way money moves. */
export function post(ctx: SimContext, p: Posting): LedgerEntry {
  invariant(Number.isSafeInteger(p.amount), `ledger amount must be an integer (${p.amount})`);
  invariant(p.account in SECTION, `unknown account ${p.account}`);
  invariant(
    ctx.state.companies.some((c) => c.id === p.companyId),
    `unknown company ${p.companyId}`,
  );
  const ledger = ctx.state.ledger;
  const entry: LedgerEntry = Object.freeze({
    id: id<"Entry">(`e-${ledger.nextSeq}`),
    companyId: p.companyId,
    time: ctx.state.time,
    account: p.account,
    amount: (p.amount === 0 ? 0 : p.amount) as Cents,
    ref: p.ref,
  });
  ledger.nextSeq++;
  ledger.entries.push(entry);
  return entry;
}

/** Freezes every entry (after a restore, entries come back as plain objects). */
export function freezeLedger(state: WorldState): void {
  for (const e of state.ledger.entries) Object.freeze(e);
}

/**
 * Incremental balance cache. Not game state: keyed by the entries array itself, so a restored
 * or rewound state (a new array) starts a fresh cache. Entries are append-only, so the cache
 * only folds the entries added since the last call.
 */
type BalanceCache = { seen: number; totals: Map<string, number> };
const caches = new WeakMap<readonly LedgerEntry[], BalanceCache>();

/** Cash position of a company: the sum of its entries (never stored). */
export function balanceOf(state: WorldState, companyId: CompanyId): Cents {
  const entries = state.ledger.entries;
  let cache = caches.get(entries);
  if (cache === undefined || cache.seen > entries.length) {
    cache = { seen: 0, totals: new Map() };
    caches.set(entries, cache);
  }
  for (; cache.seen < entries.length; cache.seen++) {
    const e = entries[cache.seen] as LedgerEntry;
    cache.totals.set(e.companyId, addSafe(cache.totals.get(e.companyId) ?? 0, e.amount));
  }
  return (cache.totals.get(companyId) ?? 0) as Cents;
}

/** Reference implementation of the balance (no cache), for tests and audits. */
export function balanceFromScratch(state: WorldState, companyId: CompanyId): Cents {
  let sum = 0;
  for (const e of state.ledger.entries) if (e.companyId === companyId) sum = addSafe(sum, e.amount);
  return sum as Cents;
}

export type IncomeStatement = {
  readonly from: GameTime;
  readonly to: GameTime;
  /** Every account touched in the period, with its total. */
  readonly byAccount: Readonly<Partial<Record<Account, Cents>>>;
  readonly revenue: Cents;
  /** Costs as a negative amount. */
  readonly costs: Cents;
  readonly result: Cents;
  /** Cash in minus cash out over the period, capex included. */
  readonly cashFlow: Cents;
};

/** Income statement and cash flow of a company over `[from, to)`: views of the ledger. */
export function incomeStatement(
  state: WorldState,
  companyId: CompanyId,
  from: GameTime,
  to: GameTime,
): IncomeStatement {
  const byAccount: Partial<Record<Account, number>> = {};
  let revenue = 0;
  let costs = 0;
  let cashFlow = 0;
  for (const e of state.ledger.entries) {
    if (e.companyId !== companyId || e.time < from || e.time >= to) continue;
    byAccount[e.account] = addSafe(byAccount[e.account] ?? 0, e.amount);
    const section = SECTION[e.account];
    if (section === "revenue") revenue = addSafe(revenue, e.amount);
    else if (section === "cost") costs = addSafe(costs, e.amount);
    if (e.account !== "equity.opening") cashFlow = addSafe(cashFlow, e.amount);
  }
  return {
    from,
    to,
    byAccount: byAccount as Partial<Record<Account, Cents>>,
    revenue: revenue as Cents,
    costs: costs as Cents,
    result: addSafe(revenue, costs) as Cents,
    cashFlow: cashFlow as Cents,
  };
}
